import { Tables } from '@/supabase/types'
import { t } from 'i18next'
import { isDev } from '../lib/utils/isDev'
import { safeConsole } from '../lib/utils/safeConsole'
import { authenticatedFetch } from '@/lib/auth/authenticated-fetch'

// Local storage keys
const PLATFORM_TOOLS_CACHE_KEY = 'cached_platform_tools'
const PLATFORM_TOOLS_TIMESTAMP_KEY = 'platform_tools_timestamp'
export const PLATFORM_TOOLS_AUTH_KEY = 'platform_tools_auth_state'
const PLATFORM_TOOLS_CACHE_TTL = 60 * 60 * 1000 // 1 hour in milliseconds
// Bump this to force-invalidate old cached tools when tool IDs/names/order change
const PLATFORM_TOOLS_CACHE_VERSION = '4' // increment to invalidate
const PLATFORM_TOOLS_VERSION_KEY = 'platform_tools_cache_version'

/**
 * Fetches platform tools, prioritizing localStorage cache.
 * Fetches from API if cache is invalid, expired, or auth state mismatches.
 */
export const getPlatformTools = async (options?: {
  isAuthenticated?: boolean
  forceRefresh?: boolean
}): Promise<Tables<'tools'>[]> => {
  const isAuthenticated = options?.isAuthenticated ?? false
  const forceRefresh = options?.forceRefresh ?? false

  if (typeof window === 'undefined') {
    // Cannot use cache server-side, always fetch
    safeConsole.log('Fetching platform tools server-side')
    return await fetchToolsFromAPI(isAuthenticated)
  }

  if (!forceRefresh) {
    try {
      const timestamp = localStorage.getItem(PLATFORM_TOOLS_TIMESTAMP_KEY)
      const cachedAuthStateStr = localStorage.getItem(PLATFORM_TOOLS_AUTH_KEY)
      const cachedVersion = localStorage.getItem(PLATFORM_TOOLS_VERSION_KEY)
      const now = Date.now()

      if (timestamp) {
        const isCacheValid = now - Number(timestamp) < PLATFORM_TOOLS_CACHE_TTL
        // Allow null/undefined cachedAuthState to match non-authenticated user
        const isAuthMatch =
          cachedAuthStateStr === String(isAuthenticated) ||
          (cachedAuthStateStr === null && !isAuthenticated)
        const isVersionMatch = cachedVersion === PLATFORM_TOOLS_CACHE_VERSION

        if (isCacheValid && isAuthMatch && isVersionMatch) {
          const cachedTools = localStorage.getItem(PLATFORM_TOOLS_CACHE_KEY)
          if (cachedTools) {
            try {
              const tools = JSON.parse(cachedTools)
              if (Array.isArray(tools) && tools.length > 0) {
                safeConsole.log('Using valid localStorage cache for platform tools')
                return tools
              }
            } catch (parseError) {
              safeConsole.warn('Error parsing cached platform tools:', parseError)
              // Cache is corrupted, proceed to fetch
              localStorage.removeItem(PLATFORM_TOOLS_CACHE_KEY)
              localStorage.removeItem(PLATFORM_TOOLS_TIMESTAMP_KEY)
              localStorage.removeItem(PLATFORM_TOOLS_AUTH_KEY)
              localStorage.removeItem(PLATFORM_TOOLS_VERSION_KEY)
            }
          }
        } else {
          // Log why cache wasn't used
          if (!isCacheValid) safeConsole.log('LocalStorage cache expired')
          if (!isAuthMatch)
            safeConsole.log(
              `LocalStorage cache auth state mismatch: cache=${cachedAuthStateStr}, current=${isAuthenticated}`
            )
          if (!isVersionMatch)
            safeConsole.log(
              `LocalStorage cache version mismatch: cache=${cachedVersion}, expected=${PLATFORM_TOOLS_CACHE_VERSION}`
            )
        }
      } else {
        safeConsole.log('No timestamp found for localStorage platform tools cache')
      }
    } catch (error) {
      safeConsole.warn('Error reading platform tools from cache:', error)
      // Proceed to fetch from API
    }
  }

  // Fetch from API if cache is invalid or forceRefresh is true
  if (forceRefresh) safeConsole.log('Forcing API fetch for platform tools')
  else safeConsole.log('No valid cache found, fetching platform tools from API')

  return await fetchToolsFromAPI(isAuthenticated)
}

/**
 * Fetches tools directly from API and updates the localStorage cache.
 * Use this function when you explicitly need to refresh the tools data.
 */
export const fetchToolsFromAPI = async (
  isAuthenticated: boolean = false
): Promise<Tables<'tools'>[]> => {
  safeConsole.log(`Fetching tools from API with auth state: ${isAuthenticated}`)

  try {
    const response = await authenticatedFetch('/api/chat/tools')

    if (!response.ok) {
      console.error('Failed to fetch platform tools:', response.statusText)
      throw new Error(response.statusText)
    }

    const tools: Tables<'tools'>[] = await response.json()
    safeConsole.log(`Received ${tools.length} tools from API`)

    // Update localStorage cache
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(PLATFORM_TOOLS_CACHE_KEY, JSON.stringify(tools))
        localStorage.setItem(PLATFORM_TOOLS_TIMESTAMP_KEY, Date.now().toString())
        localStorage.setItem(PLATFORM_TOOLS_AUTH_KEY, String(isAuthenticated))
        localStorage.setItem(PLATFORM_TOOLS_VERSION_KEY, PLATFORM_TOOLS_CACHE_VERSION)
        safeConsole.log(
          `Updated localStorage cache with ${tools.length} tools and auth state: ${isAuthenticated}`
        )
      } catch (cacheError) {
        safeConsole.warn('Error caching platform tools to localStorage:', cacheError)
      }
    }

    return tools
  } catch (error) {
    console.error('Error in fetchToolsFromAPI:', error)
    // Attempt to return potentially stale cache data as a fallback?
    // Or return empty array? Depends on desired behavior.
    if (typeof window !== 'undefined') {
      const cached = localStorage.getItem(PLATFORM_TOOLS_CACHE_KEY)
      if (cached) {
        try {
          return JSON.parse(cached)
        } catch {
          /* ignore */
        }
      }
    }
    return [] // Return empty array on fetch failure
  }
}

/**
 * Simple alias for fetchToolsFromAPI for clarity when refreshing.
 */
export const refreshPlatformTools = async (isAuthenticated: boolean = false) => {
  return await fetchToolsFromAPI(isAuthenticated)
}

/**
 * Gets a single tool definition by ID.
 * Assumes tools are already loaded/cached by getPlatformTools.
 *
 * NOTE: This function relies on tools potentially being cached in localStorage.
 * Consider passing the tools array directly if available for better reliability.
 */
export const platformToolDefinitionById = async (id: string): Promise<Tables<'tools'> | null> => {
  if (typeof window === 'undefined') {
    safeConsole.warn('platformToolDefinitionById called server-side, cache may not be available.')
    // Optionally attempt a direct fetch or return null
    return null
  }

  try {
    const cachedTools = localStorage.getItem(PLATFORM_TOOLS_CACHE_KEY)
    if (cachedTools) {
      const tools: Tables<'tools'>[] = JSON.parse(cachedTools)
      const tool = tools.find((t) => t.id === id)
      return tool || null
    }
  } catch (error) {
    console.error('Error getting tool definition from cache:', error)
  }

  return null // Return null if not found or error occurs
}

/**
 * Placeholder for preloading logic if needed elsewhere,
 * but primary loading should happen via getPlatformTools in JotaiGlobalState.
 */
export const preloadPlatformTools = () => {
  // Deprecated: Loading is handled by JotaiGlobalState calling getPlatformTools
  safeConsole.warn('preloadPlatformTools is deprecated.')
}

// Remove window cache declarations as they are no longer used directly here
// declare global {
//   interface Window {
//     __PLATFORM_TOOLS_CACHE?: { ... };
//     __PLATFORM_TOOLS_MEMORY_CACHE?: { ... };
//   }
// }
